/**
 * Data Domain Usage Widget (issue #1655, part of epic #1650)
 *
 * Shows per-domain service counts and steward-assignment status, matching
 * `homepage-dashboard-spec.md`'s "Data Domain Usage" widget. Administrators
 * see every domain; other users see only domains with at least one service
 * owned by one of their own teams (see `backend/src/graphql/resolvers/
 * dataDomainUsage.ts`). Follows the single-query + `useStatisticsWidget`
 * pattern shared by `DependencyHealthWidget`.
 */

import { useDataDomainUsage, type DataDomainUsageEntry } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './DataDomainUsageWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'data-domain-usage',
  title: 'Data Domain Usage',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface DataDomainUsageStats {
  domains: DataDomainUsageEntry[];
  unassignedStewards: number;
}

type DataDomainUsageQueryData = ReturnType<typeof useDataDomainUsage>['data'];

function extractDataDomainUsageStats(data: DataDomainUsageQueryData): DataDomainUsageStats | null {
  if (!data?.dataDomainUsage) {
    return null;
  }
  return data.dataDomainUsage;
}

export function DataDomainUsageWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useDataDomainUsage(),
    extractDataDomainUsageStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.domains.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="data-domain-usage-widget__empty">
          <IconElement name="storage" className="data-domain-usage-widget__empty-icon" />
          <p className="forge-typography--body2">No data domain usage available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      {stats.unassignedStewards > 0 && (
        <p
          className="data-domain-usage-widget__banner forge-typography--caption"
          data-testid="data-domain-usage-unassigned-banner"
        >
          <IconElement name="warning" aria-hidden="true" />
          {stats.unassignedStewards} domain{stats.unassignedStewards === 1 ? '' : 's'} with no
          steward assigned
        </p>
      )}
      <ul className="data-domain-usage-widget__list" data-testid="data-domain-usage-list">
        {stats.domains.map((domain) => (
          <li
            key={domain.id}
            className="data-domain-usage-widget__row"
            data-testid="data-domain-usage-row"
          >
            <div className="data-domain-usage-widget__row-header">
              <span className="data-domain-usage-widget__name">{domain.name}</span>
              <span className="data-domain-usage-widget__count">
                {domain.serviceCount} service{domain.serviceCount === 1 ? '' : 's'}
              </span>
            </div>
            <div className="data-domain-usage-widget__meta">
              <span className="data-domain-usage-widget__classification forge-typography--caption">
                {domain.classification}
              </span>
              {domain.warning && (
                <span
                  className="data-domain-usage-widget__warning"
                  data-testid="data-domain-usage-warning"
                >
                  {domain.warning}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </DashboardWidget>
  );
}
